import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { CheckRounded, CloseRounded } from '@mui/icons-material'
import { H3 } from '../../../layouts/TextStyles'
import { tableStyles, tone, ui } from '../../../utils/uiTokens'
import { capitalize } from '../../../utils/functions'
import { mentorReportApi, MrCell, MrComplianceRow } from '../../../features/curator/mentorReport'
import { HeadFilters, useHeadFilters, useHeadIndex } from '../headData'
import PsReportView from '../../curator/mentorReportPage/PsReportView'
import RatingReportView from '../../curator/mentorReportPage/RatingReportView'
import { Hint, page, useDefaultCourse } from '../../curator/mentorReportPage/shared'

type Tab = 'compliance' | 'ps' | 'rating'
const TABS: Tab[] = ['compliance', 'ps', 'rating']

const tabSx = (active: boolean) => ({
    padding: '8px 14px',
    borderRadius: ui.radius,
    cursor: 'pointer',
    userSelect: 'none' as const,
    fontSize: '13.5px',
    fontWeight: active ? 600 : 400,
    color: active ? ui.pink : ui.sub,
    backgroundColor: active ? ui.pinkSoft : 'transparent',
    border: `1px solid ${active ? 'transparent' : ui.border}`,
})

// Пән басшы и сервис басшы: «Есептер» - кто не заполнил (менторы × недели), и сами отчёты только для чтения.
// Группы, предметы и ментор - из фильтров басшы; предмет директора приходит через scope.
const HeadReportsPage = () => {
    const { formatMessage } = useIntl()
    const f = useHeadFilters()
    useDefaultCourse(f)
    const index = useHeadIndex()
    const [tab, setTab] = useState<Tab>('compliance')

    return (
        <Box sx={{ margin: { xs: '6px', md: '10px' } }}>
            <Box sx={page}>
                <H3>{formatMessage({ id: 'mrReportsTitle' })}</H3>
                <Box sx={{ color: ui.sub, fontSize: '13.5px', margin: '6px 0 18px' }}>{formatMessage({ id: 'mrReportsSub' })}</Box>
                <HeadFilters filters={f} index={index} />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', marginBottom: '18px' }}>
                    {TABS.map((t) => (
                        <Box key={t} sx={tabSx(tab === t)} onClick={() => setTab(t)}>
                            {formatMessage({ id: `mrTab_${t}` })}
                        </Box>
                    ))}
                </Box>
                {tab === 'compliance' && <ComplianceTable f={f} />}
                {tab === 'ps' && <PsReportView f={f} />}
                {tab === 'rating' && <RatingReportView f={f} />}
            </Box>
        </Box>
    )
}

const cellTone = (c: MrCell) => (c.missing ? tone.bad : c.psFilled >= c.psTotal && c.ratingFilled ? tone.ok : tone.neutral)

const ComplianceTable: React.FC<{ f: ReturnType<typeof useHeadFilters> }> = ({ f }) => {
    const { formatMessage, locale } = useIntl()
    const month = f.monthNumber || 0
    const { data, isFetching } = mentorReportApi.useFetchComplianceQuery(
        { courseId: f.courseId, groupId: f.groupId, monthNumber: month },
        { skip: !month },
    )
    // кто больше всего не заполнил - сверху
    const rows = useMemo(
        () => [...(data?.rows ?? [])].sort((a, b) => b.missingCount - a.missingCount || (a.mentorName ?? '').localeCompare(b.mentorName ?? '')),
        [data],
    )

    if (!month) return <Hint id="headPickMonth" />

    const subject = (r: MrComplianceRow) => r[`subjectTitle${capitalize(locale)}` as 'subjectTitleKz'] || r.subjectTitleRu
    const missingTotal = rows.reduce((s, r) => s + r.missingCount, 0)

    return (
        <Box>
            {isFetching && <LinearProgress sx={{ marginBottom: '12px' }} />}
            <Box sx={{ color: ui.sub, fontSize: '13.5px', marginBottom: '12px' }}>
                {formatMessage({ id: 'mrComplianceLegend' })}
                {rows.length > 0 && (
                    <Box component="span" sx={{ marginLeft: '8px', fontWeight: 600, color: missingTotal ? tone.bad.text : tone.ok.text }}>
                        {formatMessage({ id: 'mrMissingTotal' }, { n: missingTotal })}
                    </Box>
                )}
            </Box>
            {!isFetching && !rows.length && <Hint id="mrNoGroups" />}
            {rows.length > 0 && (
                <TableContainer sx={{ ...tableStyles.container, overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 640 }}>
                        <TableHead sx={tableStyles.head}>
                            <TableRow>
                                <TableCell>{formatMessage({ id: 'mrMentor' })}</TableCell>
                                <TableCell>{formatMessage({ id: 'mrGroup' })}</TableCell>
                                {[1, 2, 3, 4].map((w) => (
                                    <TableCell key={w} align="center" sx={w === f.weekNumber ? { color: `${ui.pink} !important` } : undefined}>
                                        {w} {formatMessage({ id: 'week' })}
                                    </TableCell>
                                ))}
                                <TableCell align="center">{formatMessage({ id: 'mrMissingCol' })}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={`${r.mentorId ?? 0}:${r.groupId}:${r.courseSubjectId}`} sx={tableStyles.row}>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                        {r.mentorName || (r.mentorId ? `#${r.mentorId}` : formatMessage({ id: 'mrNoMentor' }))}
                                    </TableCell>
                                    <TableCell>{[r.groupTitle, subject(r)].filter(Boolean).join(' · ')}</TableCell>
                                    {r.weeks.map((c) => {
                                        const t = cellTone(c)
                                        return (
                                            <TableCell key={c.weekNumber} align="center">
                                                <Box sx={{
                                                    display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
                                                    padding: '4px 10px', borderRadius: '10px', minWidth: 72,
                                                    color: t.text, backgroundColor: t.bg, border: `1px solid ${t.border}`,
                                                }}>
                                                    <Box sx={{ fontSize: '12.5px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                        {formatMessage({ id: 'mrPsShort' })} {c.psFilled}/{c.psTotal}
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                        {formatMessage({ id: 'mrRatingShort' })}
                                                        {c.ratingFilled ? <CheckRounded sx={{ fontSize: 15 }} /> : <CloseRounded sx={{ fontSize: 15 }} />}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                        )
                                    })}
                                    <TableCell align="center" sx={{ fontWeight: 700, color: r.missingCount ? `${tone.bad.text} !important` : undefined }}>
                                        {r.missingCount}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    )
}

export default HeadReportsPage
